import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Image } from "react-bootstrap";
import { toast } from "react-hot-toast";
import Loader from "../../components/Loader";

// Breadcrumb
import Breadcrumb from "../../components/layout/Breadcrumb/Breadcrumb-Productdetails";

// BtnAddToCart
import BtnAddToCart from "../../components/layout/Buttons/BtnAddToCart";

// Redux API
import { useGetProductDetailsQuery } from "../../redux/api/productsApi";

// ProductAttributes
import ProductAttributes from "./ProductAttributes";

import MetaData from "../../components/layout/MetaData";
import { formatThousandsNumber } from "../../utils/numberFormat";
import EngineeringAssetsDialog from "./components/EngineeringAssetsDialog";

/**
 * 生成商品询价跳转地址
 */
const buildProductQuotePath = (productId, qty) => {
  const searchParams = new URLSearchParams();

  if (productId) {
    searchParams.set("productId", productId);
  }

  if (!isNaN(qty)) {
    searchParams.set("qty", qty);
  }

  return `/request_quote?${searchParams.toString()}`;
};

/**
 * 渲染前台商品详情页
 */
const ProductDetails = () => {
  const [showEngineeringAssets, setShowEngineeringAssets] = useState(false);
  const params = useParams();
  const { data, isLoading, error, isError } = useGetProductDetailsQuery(params?.id);
  const product = data?.product;


  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message);
    }
  }, [isError, error?.data?.message]);
  if (isLoading) return <Loader />;

  return (
    <>
      <MetaData title={product?.name} />
      <main className="px-4 lg:px-24 bg-[#F5F9FF] py-8">
        <div className="hidden md:block">
          <Breadcrumb product={product} />
        </div>
        <article className="flex flex-col lg:flex-row w-full gap-8 mt-4 md:mt-8">
          {/* Left Column: Product Identity and Pricing */}
          <section className="w-full lg:w-[350px] flex flex-col gap-6">
            {/* Top Section: Identity, Image & Add to Cart */}
            <div className="flex flex-col items-center">
              <div className="w-full max-w-[300px] mb-4 flex justify-center py-4 px-2 bg-white">
                <Image
                  src={product?.image[0] || "/img/image_not_available.png"}
                  className="max-w-full h-auto object-contain max-h-[160px] md:max-h-[200px]"
                  alt={product?.name}
                />
              </div>

              <div className="w-full max-w-[300px] mb-4 text-center text-2xl text-gray-900">
                {product?.stock > 0 ? `${formatThousandsNumber(product.stock)} in stock` : "Out of stock"}
              </div>

              <div className="w-full">
                <BtnAddToCart {...product} />
              </div>
            </div>

            {/* Bottom Section: Bulk Pricing */}
            <div className="mt-8">
              <div className="mb-4">
                <h2 className="font-bold text-lg md:text-2xl text-gray-800">Bulk Pricing</h2>
              </div>
              <div className="w-full h-[1px] bg-gray-200 mb-6"></div>
              <div className="overflow-x-auto bg-white rounded-lg shadow-sm">
                <table className="w-full text-xs md:text-sm text-center border-collapse">
                  <thead className="bg-white">
                    <tr>
                      <th className="px-2 md:px-4 py-4 text-[#3B82F6] font-bold">Qty</th>
                      <th className="px-2 md:px-4 py-4 text-[#3B82F6] font-bold border-l border-[#D7E6FF]">Unit Price</th>
                      <th className="px-2 md:px-4 py-4 text-[#3B82F6] font-bold border-l border-[#D7E6FF]">Ext Price</th>
                    </tr>
                  </thead>
                  <tbody className="bg-[#F5F9FF]">
                    {product?.bulk_Pricing?.map((price, i) => {
                      const qty = parseInt(price.Qty);
                      // 处理价格字符串，移除所有非数字和非小数点字符
                      const unitPriceStr = price?.UnitPrice?.toString().replace(/[^0-9.]/g, '') || "0";
                      const unitPrice = parseFloat(unitPriceStr);
                      /**
                       * 判断该档位是否显示 Quote：
                       * 当 UnitPrice 为 0 或空时，表示需要询价，前端显示 Quote 按钮
                       */
                      const isQuoteTier = unitPrice === 0 || unitPriceStr === "0";
                      const extPrice = (qty * unitPrice);

                      return (
                        <tr key={i} className="hover:bg-[#EAF2FF] transition-colors">
                          <td className="px-2 md:px-4 py-4 text-gray-600">{qty}</td>
                          {isQuoteTier ? (
                            <td
                              colSpan="2"
                              className="px-2 md:px-4 py-3 text-gray-600 border-l border-[#D7E6FF]"
                            >
                              <Link
                                to={buildProductQuotePath(product?._id, qty)}
                                className="inline-flex min-h-[36px] items-center justify-center rounded-md bg-[#3B82F6] px-4 py-2 text-xs md:text-sm font-bold text-white shadow-sm transition-colors hover:bg-blue-600"
                              >
                                Get a Quote
                              </Link>
                            </td>
                          ) : (
                            <>
                              <td className="px-2 md:px-4 py-4 text-gray-600 border-l border-[#D7E6FF]">
                                {isNaN(unitPrice) ? "0.00000" : unitPrice.toFixed(5)}
                              </td>
                              <td className="px-2 md:px-4 py-4 text-gray-600 border-l border-[#D7E6FF]">
                                {isNaN(extPrice) ? "$NaN" : `$${extPrice.toFixed(3)}`}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
          
          {/* Right Column: Product Specifications */}
          <section className="flex-1 bg-white border border-gray-100 p-6 md:p-10 rounded-lg shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)]">
            <div className="mb-10">
              <h2 className="font-bold text-lg md:text-2xl text-gray-900 mb-6 md:mb-8">
                {product?.name}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 md:gap-y-4">
                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">CTYEA Part Number</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.CTYEA_Part_Number}</span>
                </div>

                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4 mt-2 md:mt-0">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">Manufacturer Part Number</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.Manufacturer_Part_Number}</span>
                </div>

                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4 mt-2 md:mt-0">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">Manufacturer</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.Manufacturer}</span>
                </div>

                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4 mt-2 md:mt-0">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">Description</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.description}</span>
                </div>

                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4 mt-2 md:mt-0">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">Datasheet</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  {product?.datasheet || product?.pinoutImage || product?.footprintImage || product?.model3dPreview || product?.model3dSource ? (
                    <button
                      type="button"
                      onClick={() => setShowEngineeringAssets(true)}
                      className="text-sm text-blue-600 underline"
                    >
                      View Datasheet
                    </button>
                  ) : (
                    <span className="text-gray-800 text-sm">-</span>
                  )}
                </div>

                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4 mt-2 md:mt-0">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">Weight ( grams )</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.Weight || "-"}</span>
                </div>
              </div>
            </div>

            <ProductAttributes
              attributes={product?.product_Attributes}
              product={product}
            />
          </section>
        </article>
      </main>
      <EngineeringAssetsDialog
        show={showEngineeringAssets}
        onHide={() => setShowEngineeringAssets(false)}
        product={product}
      />
    </>
  );
};

export default ProductDetails;
